import { AppInstanceConfig, AppInstanceConfigsState } from './types';
import { RootState } from 'store/reducer';
import { createSelector } from 'reselect';
import { configSelector } from 'store/config/selectors';
import { selectResources } from 'store/resources/selectors';
import { selectBuildingUuid } from '../building/selectors';
import { ResourceBookingEnum } from './enums';
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE } from 'pages/resources/components/map-view/constants';

export const appInstanceConfigsSelector = (state: RootState): AppInstanceConfigsState => state.appInstanceConfigs;

/*
 * A config is returned once per integration location (e.g. every Essensys site), all sharing one uuid, so the uuid
 * alone does not identify an entry: prefer the requested location, then the one flagged as default, then the first.
 */
const pickDefaultLocationConfig = (
  appInstanceConfigs: AppInstanceConfig[],
  uuid: string,
  locationId?: string,
): AppInstanceConfig | undefined => {
  const sameUuidConfigs = appInstanceConfigs.filter(config => config.uuid === uuid);
  const locationIds = locationId?.split(',').filter(Boolean) ?? [];

  return (
    (locationIds.length > 0 && sameUuidConfigs.find(config => locationIds.includes(config.location_id))) ||
    sameUuidConfigs.find(config => config.location_default) ||
    sameUuidConfigs[0]
  );
};

export const selectAppInstanceConfigs = createSelector(
  [appInstanceConfigsSelector, configSelector],
  (appInstanceConfigState, config) => {
    const appInstanceConfigs = appInstanceConfigState.app_instance_configs ?? [];
    if (config.singleAppInstanceConfig && appInstanceConfigs.length) {
      const appInstanceConfigUUID = appInstanceConfigState.currentAppInstanceConfigUuid || appInstanceConfigs[0].uuid;

      return appInstanceConfigs.filter(appInstanceConfig => appInstanceConfig.uuid === appInstanceConfigUUID);
    }
    if (config.singleLocationConfig && appInstanceConfigs.length) {
      const locationId = appInstanceConfigState.currentLocationId;
      if (locationId) {
        return appInstanceConfigs.filter(appInstanceConfig => appInstanceConfig.location_id === locationId);
      }
      return [pickDefaultLocationConfig(appInstanceConfigs, appInstanceConfigs[0].uuid)];
    }

    return appInstanceConfigs;
  },
);

export const selectAppInstanceConfigByLocationId = (locationId: string) =>
  createSelector([selectAppInstanceConfigs], appInstanceConfigs =>
    appInstanceConfigs.find(appInstanceConfig => appInstanceConfig.location_id === locationId),
  );

export const selectAppInstanceConfigByBuildings = (buildingsIds: Array<string> = []) =>
  createSelector([selectAppInstanceConfigs], appInstanceConfigs =>
    appInstanceConfigs.find(appInstanceConfig => buildingsIds.includes(appInstanceConfig.location_id)),
  );

export const selectHasVerticalAdapterNotNativeOrEssensys = createSelector(
  [selectAppInstanceConfigs],
  appInstanceConfigs => {
    return appInstanceConfigs.some(appInstanceConfig => {
      const verticalAdapterName = appInstanceConfig.vertical_adapter?.name;

      return (
        verticalAdapterName !== ResourceBookingEnum.NativeAdapter &&
        verticalAdapterName !== ResourceBookingEnum.EssensysAdapter &&
        verticalAdapterName !== ResourceBookingEnum.TripleseatAdapter
      );
    });
  },
);

export const selectAppInstanceConfigsStatus = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.getAppInstanceConfigs.status,
);

export const selectAppInstanceConfigsError = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.getAppInstanceConfigs.error,
);

export const selectCurrentAppInstanceConfigUuid = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.currentAppInstanceConfigUuid,
);

export const selectCurrentAppInstanceConfig = createSelector([appInstanceConfigsSelector], appInstanceConfigState =>
  pickDefaultLocationConfig(
    appInstanceConfigState.app_instance_configs ?? [],
    appInstanceConfigState.currentAppInstanceConfigUuid,
    appInstanceConfigState.currentLocationId,
  ),
);

export const selectCurrentLocationId = createSelector([appInstanceConfigsSelector], configs => {
  return configs.currentLocationId;
});

export const selectCurrentLocationCoordinates = createSelector(
  [selectCurrentLocationId, selectAppInstanceConfigs],
  (locationId, appInstanceConfigs) => {
    const location = appInstanceConfigs.find(config => config.location_id === locationId);

    return location?.location_coordinates;
  },
);

export const selectCurrentLocationName = createSelector(
  [selectCurrentLocationId, selectAppInstanceConfigs],
  (locationId, appInstanceConfigs) => {
    const location = appInstanceConfigs.find(config => config.location_id === locationId);

    return location?.location_name;
  },
);

export const selectAllLocations = createSelector([appInstanceConfigsSelector], configs =>
  configs.app_instance_configs.reduce(
    (map: Record<string, { latitude: number; longitude: number; locationId: string }>, config) => {
      return {
        ...map,
        [config.location_id]: {
          latitude: config.location_coordinates?.latitude,
          longitude: config.location_coordinates?.longitude,
          locationId: config.location_id,
        },
      };
    },
    {},
  ),
);

export const selectAppInstanceConfigsLocations = createSelector([appInstanceConfigsSelector], configs =>
  configs.app_instance_configs.map(config => {
    return {
      latitude: config.location_coordinates?.latitude,
      longitude: config.location_coordinates?.longitude,
      locationId: config.location_id,
    };
  }),
);

export const selectCurrentBuildingLocationCoordinates = createSelector(
  [selectBuildingUuid, selectAppInstanceConfigs],
  (buildingUuid, appInstanceConfigs) => {
    const nativeLocation = appInstanceConfigs.find(
      config =>
        config?.vertical_adapter?.name === ResourceBookingEnum.NativeAdapter && config?.building?.uuid === buildingUuid,
    );

    return {
      latitude: nativeLocation?.location_coordinates?.latitude ?? DEFAULT_LATITUDE,
      longitude: nativeLocation?.location_coordinates?.longitude ?? DEFAULT_LONGITUDE,
    };
  },
);

export const selectSelectedLocations = createSelector(
  [selectAllLocations, selectResources],
  (allLocations, resources) => {
    const selectedLocations = resources?.flatMap(resource => {
      return resource.buildings
        ?.map(building => allLocations[building.uuid])
        .filter(location => location && location.locationId);
    });
    const uniqKey = 'locationId';

    return [...new Map(selectedLocations?.map(location => [location?.[uniqKey], location])).values()];
  },
);

export const selectSelectedAppInstanceConfigsLocations = createSelector([appInstanceConfigsSelector], configs => {
  return configs.selectedAppInstanceConfigsLocations;
});

export const selectIsCurrentLocationIdUpdating = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.isCurrentLocationIdUpdating,
);

export const selectOnboardUserStatus = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.onboardUser?.status,
);

export const selectOnboardUserError = createSelector(
  [appInstanceConfigsSelector],
  configs => configs.onboardUser.error,
);
